<template>
  <div class="dashboard-container">
    <!-- Progress Bar Loading -->
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="container-fluid max-w-form">
      <!-- Judul Halaman & Tombol Kembali -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <div class="page-title mb-1">
            <i class="bi bi-calendar-check-fill text-accent"></i>
            {{ isEdit ? "Perbarui Jadwal Tanding" : "Buat Jadwal Tanding" }}
          </div>
          <p class="text-secondary small mb-0">
            Atur jadwal dan partai tanding.
          </p>
        </div>

        <router-link to="/admin/tournament-schedule" class="ui-btn ui-btn--ghost mt-2 mt-sm-0">
          <i class="bi bi-arrow-left"></i> Kembali
        </router-link>
      </div>

      <!-- Formulir Utama -->
      <form @submit.prevent="submitForm" class="schedule-form">
        <!-- ═══════════════ KARTU 1: PENGATURAN ARENA & WAKTU ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">1</div>
            <div>
              <h2 class="form-card__title">Informasi Turnamen & Waktu</h2>
              <p class="form-card__subtitle">Pilih turnamen, gelanggang, dan waktu pertandingan.</p>
            </div>
          </div>

          <div class="form-card__body">
            <div class="row g-3">
              <!-- Pilih Turnamen -->
              <div class="col-md-6">
                <label class="ui-field">
                  <span class="ui-label">Turnamen / Kejuaraan <span class="text-danger">*</span></span>
                  <select
                    v-model="form.tournament_id"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.tournament_id }"
                    :disabled="isEdit"
                    required
                  >
                    <option value="" disabled>Pilih Turnamen</option>
                    <option v-for="t in allTournaments" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.tournament_id">{{ errors.tournament_id }}</div>
                </label>
              </div>

              <!-- Pilih Gelanggang / Arena -->
              <div class="col-md-6">
                <label class="ui-field">
                  <span class="ui-label">Gelanggang (Arena) <span class="text-danger">*</span></span>
                  <select
                    v-model="form.tournament_arena_id"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.tournament_arena_id }"
                    required
                  >
                    <option value="" disabled>Pilih Gelanggang</option>
                    <option v-for="arena in allArenas" :key="arena.id" :value="arena.id">
                      {{ arena.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.tournament_arena_id">{{ errors.tournament_arena_id }}</div>
                </label>
              </div>

              <!-- Tanggal Pertandingan -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Tanggal Pertandingan <span class="text-danger">*</span></span>
                  <input
                    type="date"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.scheduled_date }"
                    v-model="form.scheduled_date"
                    required
                  />
                  <div class="invalid-feedback" v-if="errors.scheduled_date">{{ errors.scheduled_date }}</div>
                </label>
              </div>

              <!-- Jam Mulai -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Jam Mulai <span class="text-danger">*</span></span>
                  <input
                    type="time"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.start_time }"
                    v-model="form.start_time"
                    required
                  />
                  <div class="invalid-feedback" v-if="errors.start_time">{{ errors.start_time }}</div>
                </label>
              </div>

              <!-- Jam Selesai (Opsional) -->
              <div class="col-md-4">
                <label class="ui-field">
                  <span class="ui-label">Jam Selesai (Opsional)</span>
                  <input
                    type="time"
                    class="ui-input"
                    :class="{ 'is-invalid': errors.end_time }"
                    v-model="form.end_time"
                  />
                  <div class="invalid-feedback" v-if="errors.end_time">{{ errors.end_time }}</div>
                </label>
              </div>

              <!-- Nomor Partai Mulai Dari -->
              <div class="col-md-12">
                <div class="p-3 bg-light rounded-3 border d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div class="d-flex align-items-center gap-2">
                    <i class="bi bi-hash fs-4 text-primary"></i>
                    <div>
                      <strong class="d-block text-dark">Nomor Partai Mulai Dari</strong>
                      <small class="text-secondary">
                        {{ latestOrderInfo || 'Nomor partai pertama yang akan diterapkan pada jadwal ini.' }}
                      </small>
                    </div>
                  </div>
                  <div style="width: 140px;">
                    <input
                      type="number"
                      min="1"
                      class="ui-input text-center fw-bold fs-6"
                      v-model.number="startOrder"
                      @input="onStartOrderChange"
                      placeholder="1"
                    />
                  </div>
                </div>
              </div>

              <!-- Catatan Jadwal -->
              <div class="col-md-12">
                <label class="ui-field">
                  <span class="ui-label">Catatan Tambahan (Opsional)</span>
                  <textarea
                    class="ui-input"
                    v-model="form.note"
                    rows="2"
                    placeholder="Contoh: Sesi Pagi Babak Penyisihan Gelanggang A"
                  ></textarea>
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ KARTU 2: FILTER & PILIHAN BABAK ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">2</div>
            <div>
              <h2 class="form-card__title">Filter & Pemilihan Babak Pertandingan</h2>
              <p class="form-card__subtitle">Pilih babak atau kelas tanding.</p>
            </div>
          </div>

          <div class="form-card__body">
            <div class="row g-3">
              <!-- Filter Kategori Pertandingan -->
              <div class="col-md-3">
                <label class="ui-field">
                  <span class="ui-label">Kategori Tanding</span>
                  <select v-model="form.match_category_id" class="ui-input">
                    <option value="">Semua Kategori</option>
                    <option v-for="mc in matchCategories" :key="mc.id" :value="mc.id">{{ mc.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Filter Kelompok Usia -->
              <div class="col-md-3">
                <label class="ui-field">
                  <span class="ui-label">Kelompok Usia</span>
                  <select v-model="form.age_category_id" class="ui-input" @change="fetchCategoryClasses">
                    <option value="">Semua Usia</option>
                    <option v-for="ac in ageCategories" :key="ac.id" :value="ac.id">{{ ac.name }}</option>
                  </select>
                </label>
              </div>

              <!-- Filter Kelas -->
              <div class="col-md-3">
                <label class="ui-field">
                  <span class="ui-label">Kelas Tanding</span>
                  <select v-model="form.category_class_id" class="ui-input">
                    <option value="">Semua Kelas</option>
                    <option v-for="cc in categoryClasses" :key="cc.class_id" :value="cc.class_id">
                      {{ cc.gender === 'female' ? 'Putri' : 'Putra' }} Kelas {{ cc.class_name }} ({{ cc.weight_min }}-{{ cc.weight_max }} kg)
                    </option>
                  </select>
                </label>
              </div>

              <!-- PILIHAN BABAK LENGKAP -->
              <div class="col-md-3">
                <label class="ui-field">
                  <span class="ui-label text-accent fw-bold">🎯 Pilih Babak Pertandingan</span>
                  <select v-model="selectedRoundLabel" class="ui-input ui-input--accent fw-bold">
                    <option value="">Semua Babak</option>
                    <option v-for="opt in listBabakLengkap" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </label>
              </div>
            </div>

            <!-- Quick Action Bar -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-primary-subtle text-primary py-2 px-3">
                  <i class="bi bi-layers-fill"></i> Total Terpilih: <strong>{{ jumlahPartaiTerpilih }}</strong> Partai
                </span>
                <span v-if="selectedRoundLabel" class="badge bg-warning-subtle text-warning-emphasis py-2 px-3">
                  Filter Babak Aktif: <strong>{{ selectedRoundLabel }}</strong>
                </span>
              </div>

              <div class="d-flex gap-2 mt-2 mt-sm-0">
                <button type="button" class="ui-btn ui-btn--sm ui-btn--ghost" @click="pilihSemuaTampil(true)">
                  <i class="bi bi-check-all"></i> Pilih Semua yang Tampil
                </button>
                <button type="button" class="ui-btn ui-btn--sm ui-btn--ghost text-danger" @click="pilihSemuaTampil(false)">
                  <i class="bi bi-x-circle"></i> Batal Pilih Semua
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ KARTU 3: DAFTAR PARTAI PERTANDINGAN ═══════════════ -->
        <section class="form-card mb-4">
          <div class="form-card__header">
            <div class="form-card__badge">3</div>
            <div>
              <h2 class="form-card__title">Daftar Partai Pertandingan</h2>
              <p class="form-card__subtitle">Centang partai yang akan dimasukkan ke jadwal ini. Nomor partai dihitung otomatis.</p>
            </div>
          </div>

          <div class="form-card__body">
            <div v-if="!allMatchesByPool.length" class="text-center py-5 text-muted">
              <i class="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
              <p class="mb-0">Belum ada data bagan/pertandingan untuk turnamen ini.</p>
              <small>Pastikan bagan pertandingan (drawing) sudah dibuat terlebih dahulu di menu Bagan Pertandingan.</small>
            </div>

            <div v-else-if="!Object.keys(groupedPoolsByClass).length" class="alert alert-warning">
              <i class="bi bi-exclamation-triangle-fill"></i> Tidak ada pertandingan yang cocok dengan filter yang dipilih. Coba pilih <strong>"Semua Babak"</strong> atau ubah filter kelas.
            </div>

            <div v-else class="match-groups-container">
              <div v-for="(pools, key) in groupedPoolsByClass" :key="key" class="class-group-card mb-4">
                <!-- Header Kelas -->
                <div class="class-group-card__header">
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-dark">{{ key.split('|')[0] }}</span>
                    <span class="badge" :class="key.split('|')[1] === 'PUTRI' ? 'bg-danger' : 'bg-primary'">
                      {{ key.split('|')[1] }}
                    </span>
                    <strong class="text-uppercase ms-1">Kelas {{ key.split('|')[2] }}</strong>
                  </div>
                </div>

                <!-- Pools -->
                <div v-for="pool in pools" :key="pool.pool_id" class="pool-table-wrapper">
                  <div class="pool-table-header">
                    <i class="bi bi-diagram-3-fill"></i> {{ pool.pool_name }}
                  </div>

                  <div class="table-responsive">
                    <table class="table align-middle table-hover mb-0">
                      <thead>
                        <tr class="table-light">
                          <th style="width: 45px" class="text-center">#</th>
                          <th style="width: 100px">No. Partai</th>
                          <th style="width: 140px">Babak</th>
                          <th><span class="corner-indicator corner-indicator--blue"></span> Sudut Biru</th>
                          <th class="text-center" style="width: 40px">VS</th>
                          <th><span class="corner-indicator corner-indicator--red"></span> Sudut Merah</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="round in pool.rounds" :key="round.round">
                          <template v-if="round.matches.length > 0">
                            <tr v-for="match in round.matches" :key="match.id" :class="{ 'row-selected': match.selected }">
                              <td class="text-center">
                                <input
                                  type="checkbox"
                                  class="form-check-input cursor-pointer"
                                  :checked="match.selected"
                                  @change="(e) => onMatchCheckChanged(match, e.target.checked)"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  class="ui-input ui-input--sm text-center fw-bold"
                                  v-model="match.match_order"
                                  :disabled="!match.selected"
                                  placeholder="-"
                                />
                              </td>
                              <td>
                                <span class="badge bg-secondary-subtle text-secondary-emphasis">
                                  {{ round.round_label || ('Babak ' + round.round) }}
                                </span>
                              </td>
                              <td>
                                <div class="athlete-cell">
                                  <strong>{{ match.participant_one?.name || 'TBD (Pemenang Babak Sebelumnya)' }}</strong>
                                  <small class="text-muted d-block">{{ match.participant_one?.contingent?.name || '-' }}</small>
                                </div>
                              </td>
                              <td class="text-center fw-bold text-muted">VS</td>
                              <td>
                                <div class="athlete-cell">
                                  <strong>{{ match.participant_two?.name || 'TBD (Pemenang Babak Sebelumnya)' }}</strong>
                                  <small class="text-muted d-block">{{ match.participant_two?.contingent?.name || '-' }}</small>
                                </div>
                              </td>
                            </tr>
                          </template>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══════════════ TOMBOL SIMPAN JADWAL ═══════════════ -->
        <div class="form-actions mb-5">
          <button type="submit" class="ui-btn ui-btn--primary ui-btn--lg" :disabled="loading || jumlahPartaiTerpilih === 0">
            <span v-if="loading" class="ui-spinner"></span>
            <i v-else class="bi bi-calendar-plus-fill"></i>
            <span>{{ isEdit ? "Perbarui Jadwal Pertandingan" : "Simpan & Buat Jadwal Pertandingan" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ratakanGalat } from '@/utils/galatForm';
import http from "@/services/http";
import { useToast } from "vue-toastification";

export default {
  name: "TournamentScheduleForm",

  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      scheduleId: null,
      allTournaments: [],
      allArenas: [],
      allMatchesByPool: [],
      matchCategories: [],
      ageCategories: [],
      categoryClasses: [],
      roundOptions: [],
      selectedRoundLabel: '',
      scheduledMap: {},
      startOrder: 1,
      latestOrderInfo: '',
      form: {
        tournament_id: "",
        match_category_id: "",
        tournament_arena_id: "",
        scheduled_date: new Date().toISOString().split('T')[0],
        start_time: "07:30",
        end_time: "17:00",
        age_category_id: "",
        category_class_id: "",
        note: "",
      },
      errors: {},
      loading: false,
      progress: 0,
    };
  },

  computed: {
    listBabakLengkap() {
      const standard = [
        { value: '1/64 Final', label: 'Babak 1/64 Final' },
        { value: '1/32 Final', label: 'Babak 1/32 Final' },
        { value: '1/16 Final', label: 'Babak 1/16 Final' },
        { value: '1/8 Final', label: 'Babak 1/8 Final' },
        { value: '1/4 Final', label: 'Babak 1/4 Final (Perempat Final)' },
        { value: 'Semifinal', label: 'Babak Semifinal' },
        { value: 'Final', label: 'Babak Final' },
      ];

      const custom = this.roundOptions.filter(
        (ro) => !standard.some((s) => s.value.toLowerCase() === ro.value.toLowerCase())
      );

      return [...standard, ...custom];
    },

    jumlahPartaiTerpilih() {
      let count = 0;
      this.allMatchesByPool.forEach((pool) => {
        pool.rounds.forEach((round) => {
          round.matches.forEach((match) => {
            if (match.selected) count++;
          });
        });
      });
      return count;
    },

    filteredMatchesByPool() {
      return this.allMatchesByPool.map((pool) => {
        return {
          ...pool,
          rounds: (pool.rounds || [])
            .slice()
            .sort((a, b) => a.round - b.round)
            .map((round) => {
              const filteredMatches = (round.matches || []).filter((match) => {
                const matchCatId = match.match_category_id || pool.match_category_id;
                const ageCatId = match.age_category_id || pool.age_category_id;
                const classId = match.category_class_id || pool.category_class_id;

                const matchCatOk = !this.form.match_category_id || String(matchCatId) === String(this.form.match_category_id);
                const ageCatOk = !this.form.age_category_id || String(ageCatId) === String(this.form.age_category_id);
                const classOk = !this.form.category_class_id || String(classId) === String(this.form.category_class_id);

                // Normalisasi pencocokan babak
                let roundOk = true;
                if (this.selectedRoundLabel) {
                  const target = this.selectedRoundLabel.toLowerCase().trim();
                  const current = String(round.round_label || '').toLowerCase().trim();
                  roundOk = current === target;
                }

                return matchCatOk && ageCatOk && classOk && roundOk;
              });

              return {
                round: round.round,
                round_label: round.round_label,
                matches: filteredMatches,
              };
            })
            .filter((r) => r.matches.length > 0),
        };
      }).filter((pool) => pool.rounds.some((r) => r.matches.length > 0));
    },

    groupedPoolsByClass() {
      const group = {};
      this.filteredMatchesByPool.forEach((pool) => {
        const key = `${pool.age_category_name || '-'}|${pool.gender || '-'}|${pool.class_name || '-'}`;
        if (!group[key]) group[key] = [];
        group[key].push(pool);
      });
      return group;
    },
  },

  watch: {
    'form.tournament_id': {
      async handler(newId) {
        if (!newId) return;
        await this.muatKategoriEvent(newId);
        await this.fetchArenasByTournament(newId);
        await this.fetchRounds(newId);
        await this.fetchMatchList(newId);
        this.fetchCategoryClasses();
        this.fetchNextOrder();
      },
    },
    'form.tournament_arena_id': function () {
      this.fetchNextOrder();
    },
    'form.match_category_id': function () {
      this.fetchCategoryClasses();
      this.updateMatchOrder();
    },
    'form.age_category_id': function () {
      this.fetchCategoryClasses();
      this.updateMatchOrder();
    },
    'form.category_class_id': function () {
      this.updateMatchOrder();
    },
    selectedRoundLabel: function () {
      this.updateMatchOrder();
    },
    'form.start_time': function () {
      this.updateMatchOrder();
    },
  },

  async mounted() {
    this.scheduleId = this.$route.params.id;
    await this.muatDataAwal();
    if (this.isEdit && this.scheduleId) {
      await this.fetchScheduleDetail(this.scheduleId);
    }
  },

  methods: {
    /** Kategori & usia hanya yang dibuka di event terpilih. */
    async muatKategoriEvent(id) {
      if (!id) return;
      try {
        const [mc, ac] = await Promise.all([
          http.get('/fetch-match-categories', { params: { tournament_id: id, untuk: 'drawing' } }),
          http.get('/fetch-age-categories', { params: { tournament_id: id } }),
        ]);
        const semua = Array.isArray(mc.data) ? mc.data : mc.data?.data || [];
        this.matchCategories = semua.filter((c) => ((c.nomor || (/tanding/i.test(c.name) ? 'Tanding' : '')) === 'Tanding'));
        this.ageCategories = Array.isArray(ac.data) ? ac.data : ac.data?.data || [];
      } catch (e) {
        console.debug('Kategori event gagal dimuat:', e);
      }
    },

    async muatDataAwal() {
      try {
        const resTournaments = await http.get('/tournaments', { params: { fetch_all: true } });

        this.allTournaments = Array.isArray(resTournaments.data) ? resTournaments.data : resTournaments.data?.data || [];

        if (this.allTournaments.length && !this.form.tournament_id) {
          this.form.tournament_id = this.allTournaments[0].id;
        } else if (this.form.tournament_id) {
          await this.muatKategoriEvent(this.form.tournament_id);
        }
      } catch (err) {
        console.debug('Init error handled:', err);
      }
    },

    async fetchArenasByTournament(tournamentId) {
      if (!tournamentId) return;
      try {
        const { data } = await http.get(`/tournaments/${tournamentId}/arenas`);
        this.allArenas = data.data || data || [];
        if (this.allArenas.length && !this.form.tournament_arena_id) {
          this.form.tournament_arena_id = this.allArenas[0].id;
        }
      } catch (err) {
        this.allArenas = [];
      }
    },

    async fetchRounds(tournamentId) {
      if (!tournamentId) return;
      try {
        const { data } = await http.get(`/tournaments/${tournamentId}/available-rounds`);
        const rounds = data?.rounds || (typeof data === 'object' && !Array.isArray(data) ? data : {});
        this.roundOptions = Object.entries(rounds).map(([key, label]) => ({
          value: key,
          label: typeof label === 'string' ? label : key,
        }));
      } catch (err) {
        this.roundOptions = [];
      }
    },

    async fetchCategoryClasses() {
      if (!this.form.age_category_id || !this.form.tournament_id) {
        this.categoryClasses = [];
        return;
      }
      try {
        const params = {
          age_category_id: this.form.age_category_id,
          tournament_id: this.form.tournament_id,
        };
        if (this.form.match_category_id) {
          params.match_category_id = this.form.match_category_id;
        }
        const { data } = await http.get(`/fetch-available-class`, { params });
        this.categoryClasses = data || [];
      } catch (err) {
        this.categoryClasses = [];
      }
    },

    async fetchMatchList(tournamentId) {
      if (!tournamentId) return;
      try {
        const params = {};
        if (this.isEdit) params.include_scheduled = true;

        const { data } = await http.get(`/tournaments/${tournamentId}/matches`, { params });
        const pools = data?.data || data || [];

        if (Array.isArray(pools)) {
          this.allMatchesByPool = pools.map((pool) => ({
            pool_id: pool.pool_id,
            pool_name: pool.pool_name,
            class_name: pool.class_name,
            age_category_name: pool.age_category_name,
            match_category_id: pool.match_category_id,
            age_category_id: pool.age_category_id,
            category_class_id: pool.category_class_id,
            gender: pool.gender === 'male' ? 'PUTRA' : 'PUTRI',
            rounds: (pool.rounds || []).map((round) => ({
              round: round.round,
              round_label: round.round_label,
              matches: (round.matches || []).map((match) => ({
                ...match,
                match_category_id: pool.match_category_id || match.match_category_id,
                age_category_id: pool.age_category_id || match.age_category_id,
                category_class_id: pool.category_class_id || match.category_class_id,
                selected: this.isEdit ? Boolean(this.scheduledMap[match.id]) : true,
                match_order: this.scheduledMap[match.id]?.match_order || '',
                match_time: this.scheduledMap[match.id]?.match_time || this.form.start_time || '07:30',
                participant_one: match.participant_one || { name: 'Pemenang Babak Sebelumnya', contingent: { name: '-' } },
                participant_two: match.participant_two || { name: 'Pemenang Babak Sebelumnya', contingent: { name: '-' } },
              })),
            })),
          }));

          this.$nextTick(() => {
            if (!this.isEdit) {
              this.updateMatchOrder();
            }
          });
        }
      } catch (err) {
        this.allMatchesByPool = [];
      }
    },

    onStartOrderChange() {
      this.updateMatchOrder();
    },

    async fetchNextOrder() {
      if (!this.form.tournament_id || this.isEdit) return;
      try {
        const { data } = await http.get('/match-schedules/next-order', {
          params: {
            tournament_id: this.form.tournament_id,
            tournament_arena_id: this.form.tournament_arena_id || undefined,
          }
        });
        if (data && data.next_order) {
          this.startOrder = data.next_order;
          this.latestOrderInfo = data.info || '';
          this.updateMatchOrder();
        }
      } catch (err) {
        console.debug('Failed to fetch next order', err);
      }
    },

    onMatchCheckChanged(match, checked) {
      if (match) match.selected = checked;
      this.updateMatchOrder();
    },

    pilihSemuaTampil(selected) {
      this.filteredMatchesByPool.forEach((pool) => {
        pool.rounds.forEach((round) => {
          round.matches.forEach((match) => {
            match.selected = selected;
          });
        });
      });
      this.updateMatchOrder();
    },

    updateMatchOrder() {
      // Partai yang tersembunyi oleh filter tidak ikut terpilih / tersimpan.
      const tampil = new Set();
      this.filteredMatchesByPool.forEach((p) => p.rounds.forEach((r) => r.matches.forEach((m) => tampil.add(m.id))));
      this.allMatchesByPool.forEach((p) => p.rounds.forEach((r) => r.matches.forEach((m) => {
        if (m.selected && !tampil.has(m.id)) m.selected = false;
      })));

      let order = Number(this.startOrder) || 1;
      const [startHour, startMin] = (this.form.start_time || '07:30').split(':').map(Number);
      const currentTime = new Date();
      currentTime.setHours(startHour || 7, startMin || 30, 0, 0);

      this.allMatchesByPool.forEach((pool) => {
        pool.rounds.forEach((round) => {
          round.matches.forEach((match) => {
            if (match.selected) {
              match.match_order = order++;
              const hh = String(currentTime.getHours()).padStart(2, '0');
              const mm = String(currentTime.getMinutes()).padStart(2, '0');
              match.match_time = `${hh}:${mm}`;
              currentTime.setMinutes(currentTime.getMinutes() + 5);
            } else {
              match.match_order = '';
            }
          });
        });
      });
    },

    async fetchScheduleDetail(id) {
      this.loading = true;
      try {
        const { data } = await http.get(`/match-schedules/${id}`);
        const detail = data?.data || data;
        if (!detail) return;

        this.form = {
          tournament_id: detail.tournament_id,
          tournament_arena_id: detail.tournament_arena_id,
          scheduled_date: detail.scheduled_date || '',
          start_time: detail.start_time ? detail.start_time.substring(0, 5) : '07:30',
          end_time: detail.end_time ? detail.end_time.substring(0, 5) : '17:00',
          age_category_id: detail.age_category_id || '',
          match_category_id: detail.match_category_id || '',
          category_class_id: '',
          note: detail.note || '',
        };

        this.scheduledMap = {};
        const loadedOrders = [];
        (detail.details || []).forEach((d) => {
          if (d.tournament_match_id) {
            this.scheduledMap[d.tournament_match_id] = {
              match_order: d.order,
              match_time: d.start_time ? d.start_time.substring(0, 5) : '',
            };
            if (d.order) loadedOrders.push(Number(d.order));
          }
        });
        if (loadedOrders.length) {
          this.startOrder = Math.min(...loadedOrders);
        }

        await this.fetchMatchList(detail.tournament_id);
      } catch (err) {
        this.toast.error('Gagal memuat rincian jadwal.');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      const selectedMatches = [];

      this.allMatchesByPool.forEach((pool) => {
        pool.rounds.forEach((round) => {
          round.matches.forEach((match) => {
            if (match.selected) {
              selectedMatches.push({
                tournament_match_id: match.id,
                order: Number(match.match_order) || 1,
                start_time: match.match_time || this.form.start_time || '07:30',
                round_label: round.round_label || '',
                note: `Partai ${match.participant_one?.name || '-'} vs ${match.participant_two?.name || '-'}`,
              });
            }
          });
        });
      });

      if (selectedMatches.length === 0) {
        this.toast.error('Silakan centang minimal 1 partai pertandingan.');
        return;
      }

      this.loading = true;
      this.progress = 40;

      const payload = {
        tournament_id: this.form.tournament_id,
        tournament_arena_id: this.form.tournament_arena_id,
        scheduled_date: this.form.scheduled_date,
        start_time: this.form.start_time,
        end_time: this.form.end_time || null,
        note: this.form.note || null,
        age_category_id: this.form.age_category_id || null,
        match_category_id: this.form.match_category_id || null,
        round_label: this.selectedRoundLabel || '',
        matches: selectedMatches,
      };

      try {
        const endpoint = this.isEdit ? `/match-schedules/${this.scheduleId}` : '/match-schedules';
        const method = this.isEdit ? 'put' : 'post';

        await http[method](endpoint, payload);

        this.toast.success(this.isEdit ? 'Jadwal pertandingan berhasil diperbarui!' : 'Jadwal pertandingan berhasil dibuat!');
        this.$router.push('/admin/tournament-schedule');
      } catch (err) {
        if (err.response?.status === 422) {
          this.errors = ratakanGalat(err.response.data.errors || {});
          const msg = Object.values(this.errors).flat().join(' ');
          this.toast.error(msg || 'Validasi formulir jadwal gagal.');
        } else {
          this.toast.error(err.response?.data?.message || 'Gagal menyimpan jadwal pertandingan.');
        }
      } finally {
        this.loading = false;
        this.progress = 100;
      }
    },
  },
};
</script>

<style scoped>
.max-w-form {
  max-width: 1140px;
  margin-inline: auto;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.form-card {
  background: var(--bg-surface, #ffffff);
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.05));
  overflow: hidden;
}

.form-card__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-sunken, #f9fafb);
  border-bottom: 1px solid var(--border-subtle, #e5e7eb);
}

.form-card__badge {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent, #e84808);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 800;
}

.form-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
}

.form-card__subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: var(--text-secondary, #6b7280);
}

.form-card__body {
  padding: var(--space-5);
}

.ui-input--accent {
  border-color: var(--accent, #e84808);
  background-color: rgba(232, 72, 8, 0.03);
}

.class-group-card {
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}

.class-group-card__header {
  padding: 0.75rem 1rem;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.pool-table-wrapper {
  margin: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.pool-table-header {
  padding: 0.5rem 1rem;
  background: #f8fafc;
  font-weight: 700;
  font-size: 0.88rem;
  color: var(--text-primary);
  border-bottom: 1px solid #e2e8f0;
}

.corner-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}

.corner-indicator--blue {
  background: #2563eb;
}

.corner-indicator--red {
  background: #dc2626;
}

.athlete-cell strong {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.row-selected {
  background-color: rgba(232, 72, 8, 0.04) !important;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding-top: var(--space-2);
}

.progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: var(--accent, #e84808);
  z-index: 9999;
}
</style>